import { createApi } from "@reduxjs/toolkit/query/react";
import {
  Menu,
  MenuFormData,
  MenuItem,
  MenuItemFormData,
  MenuItemOption,
  MenuItemOptionDetail,
  MenuItemOptionFormData,
  MenuStatusFormData,
  Product,
  Section,
  SectionFormData,
} from "./menu.type";
import { CollectionData, PaginationResults, TypeQuery } from "@/lib/type";
import { baseQueryWithLogout } from "@/lib/baseQuery";

export const MenuApi = createApi({
  reducerPath: "menuApi",
  tagTypes: ["menu", "menus", "menuItemOption", "products"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getMenus: build.query<PaginationResults<Menu>, TypeQuery>({
      query: ({ restaurantId, ...query }) => ({
        url: restaurantId ? `menus?restaurant.uuid=${restaurantId}` : "menus",
        method: "GET",
        params: {
          ...query,
        },
      }),
      providesTags: ["menus"],
      transformResponse: (data: CollectionData<Menu>) => {
        return {
          results: data["hydra:member"],
          count: data["hydra:totalItems"],
        };
      },
    }),

    createMenu: build.mutation<Menu, MenuFormData>({
      query: (data) => {
        return {
          url: "menus",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["menus"],
    }),

    updateMenu: build.mutation<Menu, { id: string; data: string }>({
      query: ({ id, data }) => {
        return {
          url: `menus/${id}`,
          method: "PATCH",
          body: data,
          headers: {
            "Content-Type": "application/merge-patch+json",
          },
        };
      },
      invalidatesTags: ["menus", "menu"],
    }),

    changeMenuStatus: build.mutation<
      Menu,
      { id: string; data: MenuStatusFormData }
    >({
      query: ({ id, data }) => {
        return {
          url: `menus/${id}/status`,
          method: "PATCH",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/merge-patch+json",
            accept: "application/ld+json",
          },
        };
      },
      invalidatesTags: ["menus", "menu"],
    }),

    getMenuById: build.query<Menu, string>({
      query: (id) => `menus/${id}`,
      providesTags: ["menu"],
    }),

    deleteMenu: build.mutation<Menu, string>({
      query: (id) => ({
        url: `menus/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menus"],
    }),

    // Menu Section
    addSection: build.mutation<Section, { data: SectionFormData | FormData }>({
      query: ({ data }) => {
        return {
          url: `menu_sections/`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["menu"],
    }),

    addEditSection: build.mutation<
      Section,
      { id?: string; data: SectionFormData }
    >({
      query: ({ id, data }) => {
        if (id) {
          return {
            url: `menu_sections/${id}`,
            method: "PATCH",
            body: JSON.stringify(data),
            headers: {
              "Content-Type": "application/merge-patch+json",
            },
          };
        }
        return {
          url: `menu_sections`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["menu"],
    }),

    deleteSection: build.mutation<Menu, string>({
      query: (id) => ({
        url: `menu_sections/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menu"],
    }),

    duplicateSection: build.mutation<Menu, string>({
      query: (id) => ({
        url: `menu_sections/${id}/duplicate`,
        method: "POST",
        body: JSON.stringify({}),
        headers: {
          accept: "application/ld+json",
          "Content-Type": "application/ld+json",
        },
      }),
      invalidatesTags: ["menu"],
    }),

    // menu_item
    createOrUpdateMenuItem: build.mutation<
      MenuItem,
      { id?: string; data: MenuItemFormData | FormData }
    >({
      query: ({ id, data }) => {
        if (id) {
          return {
            url: `menu_items/${id}`,
            method: "PUT",
            body: JSON.stringify(data),
            headers: {
              "Content-Type": "application/ld+json",
            },
          };
        }
        return {
          url: "menu_items",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["menu"],
    }),

    changeMenuItemAvailability: build.mutation<
      MenuItem,
      { id: string; isAvailable: boolean }
    >({
      query: ({ id, isAvailable }) => ({
        url: `menu_items/${id}`,
        method: "PATCH",
        body: JSON.stringify({ isAvailable }),
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }),
      invalidatesTags: ["menu"],
    }),

    getMenuItemById: build.query<MenuItem, string>({
      query: (id) => `menu_items/${id}`,
    }),

    deleteMenuItem: build.mutation<Menu, string>({
      query: (id) => ({
        url: `menu_items/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menu"],
    }),

    duplicateMenuItem: build.mutation<Menu, string>({
      query: (id) => ({
        url: `menu_items/${id}/duplicate`,
        method: "POST",
        body: JSON.stringify({}),
        headers: {
          accept: "application/ld+json",
          "Content-Type": "application/ld+json",
        },
      }),
      invalidatesTags: ["menu"],
    }),

    // menu_item options
    getMenuItemOptions: build.query<
      PaginationResults<MenuItemOption>,
      TypeQuery
    >({
      query: ({ restaurantId, ...query }) => ({
        url: restaurantId
          ? `menu_item_options?restaurant.uuid=${restaurantId}`
          : "menu_item_options",
        method: "GET",
        params: {
          ...query,
        },
      }),
      providesTags: ["menuItemOption"],
      transformResponse: (data: CollectionData<MenuItemOption>) => {
        return {
          results: data["hydra:member"],
          count: data["hydra:totalItems"],
        };
      },
    }),

    createOrUpdateMenuItemOption: build.mutation<
      Menu,
      { id?: string; data: MenuItemOptionFormData }
    >({
      query: ({ id, data }) => {
        if (id) {
          return {
            url: `menu_item_options/${id}`,
            method: "PUT",
            body: JSON.stringify({
              "@id": `/api/menu_item_options/${id}`,
              ...data,
            }),
            headers: {
              "Content-Type": "application/ld+json",
              accept: "application/ld+json",
            },
          };
        }

        return {
          url: "menu_item_options",
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/ld+json",
            accept: "application/ld+json",
          },
        };
      },
      invalidatesTags: ["menuItemOption"],
    }),

    deleteMenuItemOption: build.mutation<MenuItemOption, string>({
      query: (id) => ({
        url: `menu_item_options/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menuItemOption"],
    }),

    getMenuItemOption: build.query<MenuItemOptionDetail, string>({
      query: (id) => `menu_item_options/${id}`,
      providesTags: ["menuItemOption"],
    }),

    // The restaurant's dish catalogue, for placing an existing dish on
    // another menu rather than recreating it (which is what used to fragment
    // reporting).
    getProducts: build.query<
      PaginationResults<Product>,
      { restaurantId: string; q?: string; page?: number; limit?: number }
    >({
      // The API renames API Platform's items-per-page parameter to `limit`
      // (config/packages/api_platform.yaml), so `itemsPerPage` would be ignored.
      query: ({ restaurantId, q, page, limit }) => ({
        url: `restaurants/${restaurantId}/products`,
        method: "GET",
        params: {
          ...(q ? { title: q } : {}),
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
        },
      }),
      providesTags: ["products"],
      transformResponse: (data: CollectionData<Product>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    // One catalogue dish, for pickers whose selected dish is not on the
    // loaded page (SFRESTO-166).
    getProduct: build.query<Product, string>({
      query: (id) => `products/${id}`,
      providesTags: ["products"],
    }),

    // Create a dish in the catalogue, independent of any menu. Placing it on a
    // menu (via "Plat existant") then reuses it, keeping sales/recipe/reporting
    // grouped under one dish.
    createProduct: build.mutation<
      Product,
      {
        restaurantId: string;
        data: { title: string; description?: string; basePrice: number; image?: string };
      }
    >({
      query: ({ restaurantId, data }) => ({
        url: `restaurants/${restaurantId}/products`,
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/ld+json",
        },
      }),
      invalidatesTags: ["products", "menu"],
    }),

    deleteProduct: build.mutation<void, string>({
      query: (id) => ({
        url: `products/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["products", "menu"],
    }),

    // The dish's identity (name/description/image/base price) lives on the
    // Product, so editing it here reflects on every menu the dish is placed on
    // — instead of diverging per placement. Invalidates "menu" so the embedded
    // product refreshes on the menu read.
    updateProduct: build.mutation<
      Product,
      { id: string; data: { title?: string; description?: string; basePrice?: number; image?: string } }
    >({
      query: ({ id, data }) => ({
        url: `products/${id}`,
        method: "PATCH",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }),
      invalidatesTags: ["menu", "products"],
    }),

    getActiveMenu: build.query<Menu, string>({
      query: (restaurantId) => `restaurants/${restaurantId}/active_menu`,
      providesTags: ["menu"],
    }),
  }),
});

export const {
  useGetMenusQuery,
  useCreateMenuMutation,
  useDeleteMenuMutation,
  useGetMenuByIdQuery,
  useGetMenuItemOptionsQuery,
  useGetMenuItemOptionQuery,
  useCreateOrUpdateMenuItemOptionMutation,
  useDeleteMenuItemOptionMutation,
  useAddEditSectionMutation,
  useCreateOrUpdateMenuItemMutation,
  useDeleteMenuItemMutation,
  useDeleteSectionMutation,
  useUpdateMenuMutation,
  useDuplicateMenuItemMutation,
  useDuplicateSectionMutation,
  useAddSectionMutation,
  useChangeMenuStatusMutation,
  useChangeMenuItemAvailabilityMutation,
  useGetMenuItemByIdQuery,
  useLazyGetMenuItemByIdQuery,
  useGetActiveMenuQuery,
  useGetProductsQuery,
  useGetProductQuery,
  useUpdateProductMutation,
  useCreateProductMutation,
  useDeleteProductMutation,
} = MenuApi;
